import { useMemo } from 'react';

import { paths } from 'src/routes/paths';

import { restrictiveFilterNavigation } from 'src/utils/permissions';

import { useAuthContext } from 'src/auth/hooks';

import SvgColor from 'src/components/svg-color';

// ----------------------------------------------------------------------

const icon = (name) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  emergency: icon('ic_emergency'),
  patient: icon('ic_patient'),
  rooms: icon('ic_rooms'),
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
  medicalServiceCatalog: icon('ic_medicalservicecatalog'),
  inventory: icon('ic_inventory_list'),
  warehouse: icon('ic_warehouse'),
  dispatchProduct: icon('ic_dispatch_product'),
  employeeRequest: icon('ic_employee_request'),
  managementEmployee: icon('ic_hhrr'),
  inventoryAnalytics: icon('ic_inventory_analytic'),
  medicalExpendableMaterials: icon('ic_medical_expendable_material'),
  products: icon('ic_products'),
  providers: icon('ic_provider_products'),
  requisition: icon('ic_requisition_doc'),
  purchaseOrder: icon('ic_purchase_order'),
  userRol: icon('ic_user_rol_permission'),
  medicalOrder: icon('ic_medicalNotes'),
  accountCatalog: icon('ic_accountable_catalog'),
  checks: icon('ic_money_check'),
  pos: icon('ic_pos'),
  entry: icon('ic_account_transaction'),
  fixedAssets: icon('ic_fixed_assets'),
  loans: icon('ic_loan'),
  dgii: icon('ic_dgii'),
  payroll: icon('ic_payroll'),
  pettyCash: icon('ic_petty_cash'),
  report: icon('ic_reports_acc'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { user } = useAuthContext();

  const rawData = useMemo(
    () => [
      // OVERVIEW
      // ----------------------------------------------------------------------
      {
        subheader: 'dashboards',
        items: [
          {
            title: 'Inicio',
            path: paths.medicore.root,
            icon: ICONS.dashboard,
          },
        ],
      },
      {
        subheader: 'Control de asistencia',
        items: [
          {
            title: 'Turnos Doctores',
            path: paths.medicore.humanResources.doctors.shift,
            icon: ICONS.job,
          },
        ],
      },
      // DOCTOR 
      // ----------------------------------------------------------------------
      {
        subheader: 'Gestión doctores',
        items: [
          {
            title: 'Planta Doctores',
            path: paths.medicore.doctorsAdministration.root,
            icon: ICONS.calendar,
          },
        ],
      },

      // PATIENT
      // ----------------------------------------------------------------------
      {
        subheader: 'Gestión de pacientes',
        items: [
          { title: 'Admisiones', icon: ICONS.patient, path: paths.medicore.admissions },
          {
            title: 'Registrar Admisión',
            icon: ICONS.patient,
            path: paths.patient.new,
          },
          {
            title: 'Pacientes',
            icon: ICONS.patient,
            path: paths.patient.list,
          },
          {
            title: 'Emergencias',
            path: paths.medicore.emergency.editor,
            icon: ICONS.emergency,
          },
          {
            title: 'Internamiento',
            path: paths.medicore.hospitalized,
            icon: ICONS.patient,
          },
          {
            title: 'UCI',
            path: paths.medicore.intensiveCareUnit,
            icon: ICONS.patient,
          },
          {
            title: 'facturación',
            path: paths.medicore.patientInvoice.list, // paths.medicore.patientInvoice.root,
            icon: ICONS.invoice,
          /*  children: [
              { title: 'facturas', path: paths.medicore.patientInvoice.list },
              { title: 'crear Factura', path: paths.medicore.patientInvoice.new },
            ], */
          },
          {
            title: 'Enfermería',
            path: paths.medicore.nurse.root,
            icon: ICONS.patient,
            children: [
              { title: 'Turnos', path: paths.medicore.nurse.shifts },
              { title: 'Solicitar productos', path: paths.medicore.nurse.requestproducts },
            ],
          },
          {
            title: 'Servicios médicos',
            path: paths.medicore.laboratories,
            icon: ICONS.rooms,
          },
          {
            title: 'Defunciones',
            path: paths.medicore.deaths,
            icon: ICONS.patient,
          },
          {
            title: 'Solicitudes de Admisión',
            path: paths.medicore.admissionRequests,
            icon: ICONS.emergency,
          },
        ],
      },
      // APPOINTMENTS
      // ----------------------------------------------------------------------
      {
        subheader: 'Consultas Médicas',
        items: [
          {
            title: 'Calendario de Citas',
            path: paths.medicore.appointmentCalendar,
            icon: ICONS.calendar,
          },
          {
            title: 'Gestión de Citas',
            path: paths.medicore.consultingRooms,
            icon: ICONS.user,
          },
          {
            title: 'Consultorios',
            path: paths.medicore.consultingRoomsRent,
            icon: ICONS.rooms,
          },
        ],
      },

      // MANAGEMENT
      // ----------------------------------------------------------------------
      {
        subheader: 'Administración',
        items: [
          {
            title: 'habitaciones/espacios',
            path: paths.medicore.rooms.root,
            icon: ICONS.rooms,
            children: [
              { title: 'Crear espacio', path: paths.medicore.rooms.new },
              { title: 'Espacios existentes', path: paths.medicore.rooms.list },
            ],
          },
          {
            title: 'Catálogo de servicios',
            path: paths.medicore.medicalServiceCatalog.root,
            icon: ICONS.medicalServiceCatalog,
          },
        ],
      },
      {
        subheader: 'Gestión de inventario',
        items: [
          {
            title: 'Analíticas de Inventario',
            path: paths.medicore.inventory.statistics,
            icon: ICONS.inventoryAnalytics,
          },
          {
            title: 'Inventario',
            path: paths.medicore.inventory.list,
            icon: ICONS.inventory,
          },
          {
            title: 'Almacenes',
            path: paths.medicore.inventory.warehouse,
            icon: ICONS.warehouse,
          },
          {
            title: 'Órdenes médicas',
            path: paths.medicore.inventory.listMedicalOrder,
            icon: ICONS.medicalOrder,
          },
          {
            title: 'Insumos médicos',
            path: paths.medicore.inventory.listExpendableMaterials,
            icon: ICONS.medicalExpendableMaterials,
          },
          {
            title: 'Órdenes de compra',
            path: paths.medicore.inventory.purchaseOrder.root,
            icon: ICONS.purchaseOrder,
            children: [
              {
                title: 'Órdenes existentes',
                path: paths.medicore.inventory.purchaseOrder.listPurchaseOrder,
              },
              {
                title: 'Crear orden',
                path: paths.medicore.inventory.purchaseOrder.newPurchaseOrder,
              },
            ],
          },
          {
            title: 'Requisiciones',
            path: paths.medicore.inventory.requisition.listRequisition,
            icon: ICONS.requisition,
          },
          {
            title: 'Productos',
            path: paths.medicore.inventory.listProduct,
            icon: ICONS.products,
          },
          {
            title: 'Proveedores',
            path: paths.medicore.inventory.listProvider,
            icon: ICONS.providers,
          },
          {
            title: 'Despachos',
            path: paths.medicore.inventory.dispatch.dispatchProduct,
            icon: ICONS.dispatchProduct,
          },
        ],
      },
      {
        subheader: 'Contabilidad',
        items: [
          {
            title: 'Catálogo de Cuentas',
            path: paths.medicore.finance.accounts.list,
            icon: ICONS.accountCatalog,
          },
          {
            title: 'Cheques',
            path: paths.medicore.finance.checks.list,
            icon: ICONS.checks,
          },
          {
            title: 'Punto de venta',
            path: paths.medicore.finance.pos,
            icon: ICONS.pos,
          },
          {
            title: 'Asientos contables',
            path: paths.medicore.finance.entry.root,
            icon: ICONS.entry,
            children: [
              { title: 'Crear asiento', path: paths.medicore.finance.entry.new },
              { title: 'Asientos', path: paths.medicore.finance.entry.list },
            ],
          },
          {
            title: 'Activos fijos',
            path: paths.medicore.finance.fixedAssets.root,
            icon: ICONS.fixedAssets,
          },
          {
            title: 'Préstamos',
            path: paths.medicore.finance.loans.root,
            icon: ICONS.loans,
          },
          {
            title: 'Facturas',
            path: paths.medicore.finance.invoices.root,
            icon: ICONS.invoice,
            children: [
              { title: 'Crear', path: paths.medicore.finance.invoices.new },
              { title: 'Lista', path: paths.medicore.finance.invoices.list },
            ],
          },
          {
            title: 'Facturación Electrónica',
            path: paths.medicore.finance.ecf.root,
            icon: ICONS.dgii,
            children: [
              { title: 'e-CF Generados', path: paths.medicore.finance.ecf.list },
              { title: 'e-CF Recibidos', path: paths.medicore.finance.ecf.listReceived},
              { title: 'Aprobaciones Recibidas', path: paths.medicore.finance.ecf.listApproves},
            ]
          },
          // {
          //   title: 'Reportes DGII',
          //   path: paths.medicore.finance.expenses_purchases.expenses,
          //   icon: ICONS.dgii,
          //   children: [
          //     { title: 'Gastos', path: paths.medicore.finance.expenses_purchases.expenses },
          //     {
          //       title: 'Ingresos',
          //       path: paths.medicore.finance.expenses_purchases.soldServicesList,
          //     },
          //   ],
          // },
          {
            title: 'Nómina',
            path: paths.medicore.finance.payroll.root,
            icon: ICONS.payroll,
          },
          {
            title: 'Caja Chica',
            path: paths.medicore.finance.pettyCash.root,
            icon: ICONS.pettyCash,
          },
          {
            title: 'Reportes',
            path: paths.medicore.finance.report.root,
            icon: ICONS.report,
            children: [
              {
                title: 'Balanza de comprobación',
                path: paths.medicore.finance.report.trialBalance,
              },
              { title: 'Estado de resultado', path: paths.medicore.finance.report.incomeStatement },
            ],
          },
        ],
      },
      {
        subheader: 'Seguros',
        items: [
          {
            title: 'Proveedores de seguros',
            path: paths.medicore.insurance.providers.root,
            icon: ICONS.rooms,
            children: [
              { title: 'Lista de proveedores', path: paths.medicore.insurance.providers.list },
            ],
          },
          {
            title: 'Auditoría de seguros',
            path: paths.medicore.insurance.audit.root,
            icon: ICONS.rooms,
            children: [
              {
                title: 'Lista de autorizaciones',
                path: paths.medicore.insurance.audit.list,
              },
            ],
          },
        ],
      },
      {
        subheader: 'Recursos Humanos',
        items: [
          {
            title: 'Gestion de personal',
            path: paths.medicore.humanResources.root,
            icon: ICONS.managementEmployee,
            children: [
              { title: 'Empleados', path: paths.medicore.humanResources.employees.list },
              { title: 'Doctores', path: paths.medicore.humanResources.doctors.list },
              {
                title: 'departamentos',
                path: paths.medicore.humanResources.departments.root,
              },
            ],
          },
          {
            title: 'Vacantes',
            path: paths.medicore.humanResources.jobPositions.root,
            icon: ICONS.job,
            children: [
              { title: 'Lista', path: paths.medicore.humanResources.jobPositions.root },
              { title: 'Nueva Vacante', path: paths.medicore.humanResources.jobPositions.new },
            ],
          },
          {
            title: 'Solicitud de Empleados',
            path: paths.medicore.employeeRequest.request,
            icon: ICONS.employeeRequest,
          },
        ],
      },
      {
        subheader: 'Configuración',
        items: [
          {
            title: 'Parámetros del Sistema',
            path: paths.medicore.configuration.root,
            icon: ICONS.menuItem,
            children: [
              {
                title: 'Categorías de Equipos',
                path: paths.medicore.configuration.equipmentCategory.list,
              },
              {
                title: 'Honorarios Médicos',
                path: paths.medicore.configuration.doctorFeesCatalog.list,
              },
              {
                title: 'Servicios Médicos',
                path: paths.medicore.configuration.doctorServicesCatalog.list,
              },
              {
                title: 'Especialidades Médicas',
                path: paths.medicore.configuration.doctorSpeciality.list,
              },
              {
                title: 'Contabilidad Dinámica',
                path: paths.medicore.configuration.dynamicAccounting.root,
              },
              {
                title: 'Adm. de impuestos',
                path: paths.medicore.configuration.taxes.root
              }
            ],
          },
        ],
      },

      // HELP DESK
      // ----------------------------------------------------------------------
      {
        subheader: 'Mesa de ayuda',
        items: [
          {
            title: 'Usuarios',
            path: paths.medicore.userManagement.list,
            icon: ICONS.user,
          },
          {
            title: 'Roles y permisos',
            path: paths.medicore.userManagement.rolesPermissions,
            icon: ICONS.userRol,
          },
        ],
      },
    ],
    []
  );

  // ⚠️ MODO ULTRA-RESTRICTIVO: Filtrar navegación con cero tolerancia
  // SOLO usuarios master ven toda la navegación
  // Otros usuarios ven EXCLUSIVAMENTE lo que sus permisos permiten
  const filteredData = useMemo(() => {
    const result = restrictiveFilterNavigation(user, rawData);

    return result;
  }, [user, rawData]);

  return filteredData;
}
